import { GameSettingType } from "@1games-ru/region-shared/dist/enums/account/settings";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { SoundButton, Tabs } from "@/shared/ui";
import useGameSettings from "@/domains/settings/hooks/useGameSettings";
import PlayerMenuSettingsBasicHudContainer from "@/domains/playerMenu/containers/settings/basic/hud/PlayerMenuSettingsBasicHudContainer";
import PlayerMenuContent from "@/domains/playerMenu/components/PlayerMenuContent";
import PlayerMenuContentTitle from "@/domains/playerMenu/components/PlayerMenuContentTitle";
import {
  appliedBasicSettingsSelector,
  reset,
  setMemoizedSettings,
} from "@/domains/settings/basic/slice";

enum Tab {
  Hud = "Hud",
  Interface = "Interface",
}

export default function PlayerMenuSettingsBasicContainer() {
  const { t } = useTranslation("playerMenu");
  const { updateGameSettings, resetGameSettings, getGameSettings } = useGameSettings();
  const [isSaveSettings, setIsSaveSettings] = useState(false);
  const [isResetSettings, setIsResetSettings] = useState(false);
  const dispatch = useAppDispatch();
  const appliedBasicSettings = useAppSelector(appliedBasicSettingsSelector);
  const [currTab, setCurrTab] = useState<Tab>(Tab.Hud);

  useEffect(() => {
    dispatch(setMemoizedSettings(appliedBasicSettings));

    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  const resetToDefaultHandler = useCallback(async () => {
    setIsResetSettings(true);
    const settings = Object.keys(appliedBasicSettings);
    const response = await resetGameSettings(settings as unknown as GameSettingType[]);

    if (response instanceof RpcMpProviderSuccessResponse) {
      await getGameSettings();
      dispatch(setMemoizedSettings(appliedBasicSettings));
    } else {
      toast.error(response.message);
    }
    setIsResetSettings(false);
  }, [dispatch, appliedBasicSettings]);

  const saveHandler = useCallback(async () => {
    setIsSaveSettings(true);
    const response = await updateGameSettings(appliedBasicSettings);

    if (response instanceof RpcMpProviderSuccessResponse) {
      dispatch(setMemoizedSettings(appliedBasicSettings));
    } else {
      toast.error(response.message);
    }
    setIsSaveSettings(false);
  }, [appliedBasicSettings, dispatch]);

  const tabs = useMemo(
    () => [
      { value: Tab.Hud, label: t(`playerMenu.tab.Basic${Tab.Hud}`) },
      { value: Tab.Interface, label: t(`playerMenu.tab.Basic${Tab.Interface}`) },
    ],
    [],
  );

  const header = (
    <div>
      <PlayerMenuContentTitle>{t("playerMenu.tabTitle.Basic")}</PlayerMenuContentTitle>
      <Tabs tabs={tabs} value={currTab} onClick={(value) => setCurrTab(value as Tab)} />
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between">
      <SoundButton
        className="h-[2.75rem]"
        loading={isSaveSettings}
        disabled={isSaveSettings || isResetSettings}
        onClick={saveHandler}
      >
        Сохранить изменения
      </SoundButton>
      <SoundButton
        variant="soft"
        color="gray"
        className="h-[2.75rem]"
        loading={isResetSettings}
        disabled={isResetSettings || isSaveSettings}
        onClick={resetToDefaultHandler}
      >
        Сбрость к стандартным
      </SoundButton>
    </div>
  );

  if (!appliedBasicSettings) {
    return null;
  }

  return (
    <PlayerMenuContent header={header} footer={footer}>
      {currTab === Tab.Hud && <PlayerMenuSettingsBasicHudContainer />}
    </PlayerMenuContent>
  );
}
